import React, { useState, useEffect, useCallback } from 'react';
import eventBus from '../core/EventBus';

/**
 * WarningToast — displays amber/orange warning toasts for collision/bleed warnings.
 * Listens to eventBus 'warning' events.
 * Persistent: stays visible until user clicks "Verstanden".
 */
function WarningToast() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((data) => {
    setToasts((prev) => {
      // Avoid duplicate messages
      if (prev.some((t) => t.message === data.message)) {
        return prev;
      }
      const id = Date.now() + Math.random();
      const next = [...prev, { id, message: data.message }];
      return next.slice(-3);
    });
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, phase: 'exit' } : t)));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  useEffect(() => {
    const unsub = eventBus.on('warning', addToast);
    return unsub;
  }, [addToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`
            flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg
            bg-amber-500 dark:bg-amber-600 text-white text-sm font-medium
            transition-all duration-300 max-w-lg
            ${t.phase === 'exit' ? 'opacity-0 translate-y-2' : 'opacity-100'}
          `}
          style={{
            animation: t.phase !== 'exit' ? 'warningSlideUp 0.3s ease-out' : undefined,
          }}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span className="flex-1">{t.message}</span>
          <button
            onClick={() => dismiss(t.id)}
            className="ml-2 px-3 py-1 rounded-lg text-xs font-bold bg-white/20 hover:bg-white/30 transition-colors whitespace-nowrap"
          >
            Verstanden
          </button>
        </div>
      ))}

      <style>{`
        @keyframes warningSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default WarningToast;
